import DataListHeader from "components/DataListHeader";
import DataTable from "components/DataTable/DataTable";
import LoadingSpinner from "components/LoadingSpinner";
import PageComponent from "components/Page/PageComponent";
import useSortOptions from "hooks/useSortOptions";
import { orderBy } from "lodash";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterService from "services/filterService";
import { arrayToObject } from "utils/object-util";
import { useGetAllVendorsQuery } from "../vmsSlice";
import { setSearchVal } from "./vendorFiltersSlice";
import { VendorOverview } from "./VendorOverview";
import { VendorFeedback } from "../vendorFeedback/VendorFeedback";
import { VendorRating } from "../vendorFeedback/VendorRating";
import { permissionProfiles } from "../../../components/Authorize/permissionProfiles";
import { Button } from "@material-ui/core";
import EditVendor from "features/vendor/EditVendor";
import { useAddVendorMutation } from "features/vendor/vendorSlice";
import Authorize from "components/Authorize/Authorize";
import NotificationProvider from "components/NotificationProvider";

export const VendorList = () => {
    const dispatch = useDispatch();

    let { data: vendors, error: vendorsError, isLoading: isLoadingVendors } = useGetAllVendorsQuery();
    vendors = vendors || [];

    const vendorsLookup = useMemo(() => {
        return arrayToObject(vendors);
    }, [vendors]);

    const { searchVal } = useSelector(state => state.vendorFilters);

    const onSearch = (val) => {
        dispatch(setSearchVal(val));
    };

    const [sortOptions, sortData] = useSortOptions();

    const [selectedVendor, setSelectedVendor] = useState(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [vendorToEdit, setVendorToEdit] = useState(null);
    const [addVendor] = useAddVendorMutation();

    const handleSave = async ({ id, name, includeFmc }) => {
        const res = await addVendor({ id, name, includeFmc });
        if (!res?.error) {
            NotificationProvider.success(id ? 'Vendor updated successfully' : 'Vendor created successfully');
        } else {
            NotificationProvider.error('Failed to save vendor');
        }
    };

    const columns = [
        { name: "Vendor Name", key: "name" },
        {
            name: "Rating", key: "avgRating", component: (row) => {
                return <VendorRating
                    value={row?.avgRating}
                    readOnly
                />
            }
        },
    ]

    const mapExportData = row => {
        const vendor = {};
        columns?.forEach(col => {
            if (col.name) {
                vendor[col.name] = row?.[col.key];
            }
        });
        return vendor;
    }

    const rowActions = (rowId) => {
        const row = vendorsLookup[rowId];
        return [
            {
                component: <Button
                    button variant='text' color="primary"
                    onClick={() => setSelectedVendor(rowId)}
                >
                    Profile
                </Button>
            },
            {
                component: <Authorize profile={permissionProfiles.VENDOR.EDIT}>
                    <Button
                        variant='text' color="primary"
                        onClick={() => { setVendorToEdit(row); setOpenDialog(true); }}
                    >
                        Edit
                    </Button>
                </Authorize>
            },
            {
                component: <VendorFeedback vendor={row} withDialog />,
                profile: permissionProfiles.VENDOR.ADD_FEEDBACK
            }
        ];
    }

    let filteredData = FilterService.filter([], searchVal, vendors);

    return (<>
        <EditVendor
            open={openDialog}
            setOpen={setOpenDialog}
            label={vendorToEdit ? 'Edit Vendor' : 'New Vendor'}
            id={vendorToEdit?.id}
            vendor={vendorToEdit}
            onSave={handleSave}
        />
        {selectedVendor ? <VendorOverview
            vendor={vendorsLookup[selectedVendor]}
            setSelectedVendor={setSelectedVendor} /> :
            <PageComponent
                header={<DataListHeader
                    title={'Vendor Directory'}
                    data={filteredData}
                    onSearch={onSearch}
                    actionButtons={
                        <Authorize profile={permissionProfiles.VENDOR.EDIT}>
                            <Button
                                variant="contained" color="primary"
                                onClick={() => { setVendorToEdit(null); setOpenDialog(true); }}
                            >
                                New Vendor
                            </Button>
                        </Authorize>
                    }
                />}
            >
                {isLoadingVendors ? <LoadingSpinner loading={isLoadingVendors} /> :
                    <DataTable
                        mapExportData={mapExportData}
                        noItemsMessage={isLoadingVendors ? 'Loading...' : 'No vendors found'}
                        columns={columns}
                        rows={orderBy(
                            filteredData,
                            sortOptions.columnToSort,
                            sortOptions.sortDirection
                        )}
                        rowIdentifier='id'
                        onSort={sortData}
                        sortDirection={sortOptions.sortDirection}
                        columnToSort={sortOptions.columnToSort}
                        title={'Vendors'}

                        actions={rowActions}
                    />}
            </PageComponent>}
    </>
    );
}